/**
 * WhatsNewFeed — shared renderer for the release-notes list.
 *
 * Used by both the What's New popup (compact) and the full What's New page.
 * Two-level accordion so a long history stays scannable:
 *   • Each RELEASE is a collapsible section (rough date + count in the header);
 *     the newest release is open by default, the rest collapsed.
 *   • Within a release, each change shows its title + type chip, and the
 *     summary is hidden behind a per-item toggle (collapsed by default).
 *
 * Theme-aware; no brand hardcodes. Pure presentation.
 */

import { useState } from 'react';
import { Sparkles, Wand2, Wrench, ChevronDown } from 'lucide-react';
import {
  WHATS_NEW_TYPE_META,
  type WhatsNewItem,
  type WhatsNewRelease,
  type WhatsNewType,
} from '@/data/whatsNew';
import { getLocaleSync } from '@/utils/locale';

/** Static class maps so Tailwind can see every class at build time. */
const TYPE_CHIP: Record<WhatsNewType, string> = {
  new: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/25',
  improved: 'bg-blue-500/10 text-blue-400 border-blue-500/25',
  fixed: 'bg-amber-500/10 text-amber-500 border-amber-500/25',
};

const TYPE_ICON = {
  new: Sparkles,
  improved: Wand2,
  fixed: Wrench,
} as const;

function formatDate(dateStr: string): string {
  // Parse YYYY-MM-DD as local (not UTC) to avoid off-by-one in AU timezones.
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const date = new Date(y, m - 1, d);
  try {
    return date.toLocaleDateString(getLocaleSync(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** A single change: title + type chip always visible, summary behind a toggle. */
function ChangeRow({ item }: { item: WhatsNewItem }) {
  const [open, setOpen] = useState(false);
  const meta = WHATS_NEW_TYPE_META[item.type];
  const Icon = TYPE_ICON[item.type];

  return (
    <div className="rounded-lg border border-theme-soft">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-surface-hover transition-colors rounded-lg"
      >
        <span
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 mt-px rounded-full text-[10px] font-bold uppercase tracking-wide border ${TYPE_CHIP[item.type]}`}
        >
          <Icon className="w-3 h-3" />
          {meta.label}
        </span>
        <span className="min-w-0 flex-1 text-sm font-medium text-theme-primary leading-snug pt-0.5">
          {item.title}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-theme-muted shrink-0 mt-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="px-3 pb-3 pt-0 text-[13px] text-theme-tertiary leading-relaxed">
          {item.description}
        </p>
      )}
    </div>
  );
}

/** A dated release: collapsible header (title + rough date + count), item list. */
function ReleaseSection({
  release,
  defaultOpen,
  isLatest,
  compact,
}: {
  release: WhatsNewRelease;
  defaultOpen: boolean;
  isLatest: boolean;
  compact: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const count = release.items.length;

  return (
    <div className="rounded-xl border border-theme bg-surface-card/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors"
      >
        <ChevronDown
          className={`w-4 h-4 text-theme-muted shrink-0 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
            <span className={`font-semibold text-theme-primary ${compact ? 'text-sm' : 'text-[15px]'}`}>
              {release.title}
            </span>
            {isLatest && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-brand-secondary/15 text-brand-secondary border border-brand-secondary/25">
                <Sparkles className="w-2.5 h-2.5" />
                Latest
              </span>
            )}
          </div>
        </div>
        <span className="shrink-0 text-right text-xs text-theme-muted leading-tight">
          <span className="block">{formatDate(release.date)}</span>
          <span className="block text-[10px]">
            {count} update{count === 1 ? '' : 's'}
          </span>
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {release.tagline && (
            <p className="px-1 pb-1 text-xs text-theme-tertiary">{release.tagline}</p>
          )}
          {release.items.map((item, i) => (
            <ChangeRow key={i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

interface WhatsNewFeedProps {
  releases: WhatsNewRelease[];
  /** Id of the newest release — gets a subtle "Latest" pill + opens by default. */
  latestId?: string;
  /** Tighter spacing for the popup. */
  compact?: boolean;
  /** Open every release by default (the full page). When false, only the
   *  newest is open (the compact popup). Change summaries stay collapsed
   *  either way. */
  defaultAllOpen?: boolean;
}

export function WhatsNewFeed({
  releases,
  latestId,
  compact = false,
  defaultAllOpen = false,
}: WhatsNewFeedProps) {
  if (!releases.length) {
    return (
      <div className="text-center py-12 text-theme-muted text-sm">
        Nothing new just yet — check back soon.
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-2.5' : 'space-y-3'}>
      {releases.map((release, idx) => (
        <ReleaseSection
          key={release.id}
          release={release}
          defaultOpen={defaultAllOpen || idx === 0}
          isLatest={latestId === release.id}
          compact={compact}
        />
      ))}
    </div>
  );
}
